import React, { useState, createContext, useContext } from "react";
import { AnimatePresence, motion } from "motion/react";
import { NavLink } from "react-router-dom";
import { IconMenu2, IconX } from "@tabler/icons-react";
import { cn } from "../../lib/utils";

const SidebarContext = createContext(undefined);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
};

export const SidebarProvider = ({
  children,
  open: openProp,
  setOpen: setOpenProp,
  animate = true,
}) => {
  const [openState, setOpenState] = useState(false);

  const open = openProp !== undefined ? openProp : openState;
  const setOpen = setOpenProp !== undefined ? setOpenProp : setOpenState;

  return (
    <SidebarContext.Provider value={{ open, setOpen, animate: animate }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const Sidebar = ({ children, open, setOpen, animate }) => {
  return (
    <SidebarProvider open={open} setOpen={setOpen} animate={animate}>
      {children}
    </SidebarProvider>
  );
};

export const SidebarBody = (props) => {
  return (
    <>
      <DesktopSb {...props} />
      <MobileSb {...props} />
    </>
  );
};

export const DesktopSb = ({ className, children, ...props }) => {
  const { animate } = useSidebar();
  return (
    <SidebarContext.Provider
      value={{ open: true, setOpen: () => {}, animate }}
    >
      <motion.div
        className={cn(
          "h-full hidden shrink-0 flex-col bg-neutral-100 px-4 py-4 md:flex dark:bg-neutral-800",
          "w-[300px]",
          className
        )}
        initial={false}
        animate={{
          width: "300px",
        }}
        {...props}
      >
        <div className="flex h-full flex-col overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {children}
        </div>
      </motion.div>
    </SidebarContext.Provider>
  );
};

export const MobileSb = ({ className, children, ...props }) => {
  const { open, setOpen } = useSidebar();
  return (
    <>
      <motion.div
        className={cn(
          "flex h-12 w-full flex-row items-center justify-between bg-neutral-900 px-4 text-white md:hidden dark:bg-neutral-800",
          className
        )}
        {...props}
      >
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 font-medium text-white"
        >
          <IconMenu2 className="text-white" />
          Открыть меню
        </button>
        <NavLink
          to="/"
          className="no-underline font-medium text-white/90 hover:text-white"
        >
          На сайт
        </NavLink>
      </motion.div>

      <AnimatePresence>
        {open && (
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[90] bg-black/50"
            onClick={() => setOpen(false)}
          />
        )}
        {open && (
          <motion.div
            key="panel"
            initial={{ x: "-100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "-100%", opacity: 0 }}
            transition={{
              ease: "easeInOut",
              duration: 0.3,
            }}
              className="fixed left-0 top-0 z-[100] flex h-full w-[280px] flex-col bg-neutral-900 p-6 text-neutral-100 shadow-xl [&_svg]:!text-white [&_span]:!text-white"
          >
            <div
              className="absolute right-4 top-4 cursor-pointer text-neutral-900 dark:text-neutral-100"
              onClick={() => setOpen(false)}
            >
              <IconX />
            </div>
            <div
              className="mt-4 flex flex-col gap-2 overflow-y-auto"
              onClick={() => setOpen(false)}
            >
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export const SidebarLink = ({ link, className, ...props }) => {
  const { open, animate } = useSidebar();
  return (
    <NavLink
      to={link.href}
      className={cn(
        "group/sidebar flex items-center justify-start gap-2 py-2 no-underline",
        className
      )}
      {...props}
    >
      {link.icon}

      <motion.span
        animate={{
          display: animate ? (open ? "inline-block" : "none") : "inline-block",
          opacity: animate ? (open ? 1 : 0) : 1,
        }}
        className="!m-0 inline-block whitespace-pre !p-0 text-sm text-neutral-900 transition duration-150 group-hover/sidebar:translate-x-1 dark:text-neutral-200"
      >
        {link.label}
      </motion.span>
    </NavLink>
  );
};

export default Sidebar;